import axios from "axios";
import { generateRandomString, sha256 } from "oslo/crypto";
import { base64url } from "oslo/encoding";
import { ErrorCategory, ErrorSeverity, createError, logError } from '@services/errorHandling';

/**
 * PKCE Flow is documented here: https://developer.zettle.com/docs/api/oauth/user-guides/set-up-app-authorisation/set-up-authorisation-code-grant-with-pkce
 */

/**
 * Returns a (probably not) cryptographically random string of length n; inteded to be used as a code verifier in a PKCE exchange
 */
export const generateCodeVerifier = (n = 127) => {
    return generateRandomString(n, "ABCDEFGHIJKLMNOPQRSTUVWXUZabcdefghijklmnopqrstuvwxyz0123456789-._~");
};

/**
 * Generates a cryptographically secure nonce for CSRF protection.
 *
 * Uses a 127-character string from a 62-character alphabet, providing
 * approximately 752 bits of entropy (127 * log2(62) ≈ 752 bits).
 *
 * This exceeds NIST recommendations for session tokens (128+ bits).
 *
 * @param n Length of the nonce (default: 127, minimum: 32 for security)
 * @returns A cryptographically random string suitable for CSRF protection
 */
export const generateNonce = (n = 127) => {
    // Enforce minimum length for security
    const length = Math.max(n, 32);

    return generateRandomString(length, "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789");
};

/**
 * Creates a Base64URL encoded SHA256 hash of a string; intended to be used to generate a code challenge from a code verifier in a PKCE exchange
 */
export const createCodeChallenge = async (codeVerifier: string) => {
    const data = new TextEncoder().encode(codeVerifier);
    return sha256(data).then((data) => {
        return base64url.encode(new Uint8Array(data), { includePadding: false });
    });
};

const ZETTLE_AUTH_URI = "https://oauth.zettle.com/authorize";

/**
 * Creates a URL which, when redirected to, prompts the user for Zettle Authentication
 */
export const generateAuthenticationUrl = (codeChallenge: string, nonce: string, redirect_uri: string) => {
    const url = new URL(ZETTLE_AUTH_URI);

    if (!process.env.ZETTLE_CLIENT_ID) {
        const error = createError(
            ErrorCategory.CONFIG,
            ErrorSeverity.CRITICAL,
            "ZETTLE_CLIENT_ID environment variable is not set"
        );
        logError(error);
        throw new Error(error.message);
    }

    const parameters = {
        response_type: "code",
        scope: "READ:PURCHASE",
        client_id: process.env.ZETTLE_CLIENT_ID,
        redirect_uri: redirect_uri,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        state: nonce,
    };

    for (const key in parameters) {
        if (Object.prototype.hasOwnProperty.call(parameters, key)) {
            url.searchParams.append(key, parameters[key as keyof typeof parameters]);
        }
    }
    return url.toString();
};

export const retriveAccessAndRefreshToken = (code: string, code_verifier: string) => {
    const redirect_uri = new URL(window.location.href);
    redirect_uri.search = "";

    if (!process.env.ZETTLE_CLIENT_ID) {
        const error = createError(
            ErrorCategory.CONFIG,
            ErrorSeverity.CRITICAL,
            "ZETTLE_CLIENT_ID environment variable is not set"
        );
        logError(error);
        throw new Error(error.message);
    }

    const params = new URLSearchParams();
    params.append("code", code);
    params.append("code_verifier", code_verifier);
    params.append("grant_type", "authorization_code");
    params.append("client_id", process.env.ZETTLE_CLIENT_ID);
    params.append("redirect_uri", redirect_uri.toString());

    return axios.post(
        "/token",
        params,
        {
            headers: {
                "Cache-Control": "no-cache",
                "Content-Type": "application/x-www-form-urlencoded",
            },
            withCredentials: true,
        },
    );
};

/**
 * Refreshes an expired access token using the refresh token.
 *
 * @param refresh_token The refresh token obtained during initial authentication
 * @returns Promise with new access token and refresh token
 */
export const refreshAccessToken = (refresh_token: string) => {
    if (!process.env.ZETTLE_CLIENT_ID) {
        const error = createError(
            ErrorCategory.CONFIG,
            ErrorSeverity.CRITICAL,
            "ZETTLE_CLIENT_ID environment variable is not set"
        );
        logError(error);
        throw new Error(error.message);
    }

    const params = new URLSearchParams();
    params.append("refresh_token", refresh_token);
    params.append("grant_type", "refresh_token");
    params.append("client_id", process.env.ZETTLE_CLIENT_ID);

    return axios.post(
        "/token",
        params,
        {
            headers: {
                "Cache-Control": "no-cache",
                "Content-Type": "application/x-www-form-urlencoded",
            },
            withCredentials: true,
        },
    );
};
